/**
 * SOLID Review
 * S — Strong. One concern: the tab's guest identity in storage — read (with the legacy migration),
 *     saved, cleared.
 * O — Good. A new legacy location is one more read in the migration.
 * L — N/A — functions.
 * I — Strong. Three functions.
 * D — Strong. The tab-persistent storage wrapper and the browser's own storage only.
 */
import { tabPersistentStorage } from '../../shared/tabPersistentStorage';

/**
 * THE GUEST IDENTITY'S STORAGE (frontend strict-ports plan, FE2, 2026-10-03), moved out of the auth store,
 * unchanged, and into Infrastructure in FE3, when the auth store's sign-in and sign-out became decisions:
 * its one consumer is `ClientTabStorage` (a returning guest, a new one, a rejoin that adopts another
 * seat's id, and a sign-in or sign-out that forgets it).
 *
 * Guest identity is kept PER-TAB but DISCARD-DURABLE via tabPersistentStorage (window.name id +
 * namespaced localStorage), mirroring the JWT. This keeps every new tab starting at WelcomeScreen (no
 * silent inheritance across tabs) while a backgrounded guest tab the OS discards comes back as the SAME
 * guest instead of being wiped to anonymous. Legacy sessionStorage guests are migrated on read.
 */
const GUEST_ID_KEY = 'meyerlive3d_guest_id';
const GUEST_NAME_KEY = 'meyerlive3d_guest_name';

export interface IEntityStoredGuest {
  readonly id: string | null;
  readonly name: string | null;
}

export function readGuest(): IEntityStoredGuest {
  try {
    let id = tabPersistentStorage.getItem(GUEST_ID_KEY);
    let name = tabPersistentStorage.getItem(GUEST_NAME_KEY);
    if (!id || !name) {
      const legacyId = sessionStorage.getItem(GUEST_ID_KEY);
      const legacyName = sessionStorage.getItem(GUEST_NAME_KEY);
      if (legacyId && legacyName) {
        tabPersistentStorage.setItem(GUEST_ID_KEY, legacyId);
        tabPersistentStorage.setItem(GUEST_NAME_KEY, legacyName);
        sessionStorage.removeItem(GUEST_ID_KEY);
        sessionStorage.removeItem(GUEST_NAME_KEY);
        id = legacyId;
        name = legacyName;
      }
    }
    const guest: IEntityStoredGuest = { id, name };
    return guest;
  } catch {
    const none: IEntityStoredGuest = { id: null, name: null };
    return none;
  }
}

export function saveGuest(playerId: string, displayName: string): void {
  try {
    tabPersistentStorage.setItem(GUEST_ID_KEY, playerId);
    tabPersistentStorage.setItem(GUEST_NAME_KEY, displayName);
    sessionStorage.removeItem(GUEST_ID_KEY); // drop any legacy per-tab copy
    sessionStorage.removeItem(GUEST_NAME_KEY);
  } catch { /* ignore */ }
}

export function clearGuestStorage(): void {
  try {
    tabPersistentStorage.removeItem(GUEST_ID_KEY);
    tabPersistentStorage.removeItem(GUEST_NAME_KEY);
    // Also wipe any legacy per-tab / shared copies.
    sessionStorage.removeItem(GUEST_ID_KEY);
    sessionStorage.removeItem(GUEST_NAME_KEY);
    localStorage.removeItem(GUEST_ID_KEY);
    localStorage.removeItem(GUEST_NAME_KEY);
  } catch { /* ignore */ }
}
